import Button, { ButtonProps } from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { FC } from "react";
import { buttons } from "../constants";
import { modals } from "../constants/text/modals";
import { EActiveInactiveStatus } from "../types/status.types";
import { LoadingButton } from "./buttons/LoadingButton";
import {
  IModalComponentProps,
  ModalComponent,
} from "./ModalComponent/ModalComponent";

export enum EConfirmModalType {
  DELETE = "DELETE",
  STATUS = "STATUS",
  WARNING = "WARNING",
}

interface LoadingButtonProps extends ButtonProps {
  isLoading: boolean;
}

export interface IConfirmModalProps extends IModalComponentProps {
  confirmBtnProps?: LoadingButtonProps;
  cancelBtnProps?: ButtonProps;
  confirmModalType?: EConfirmModalType;
  status?: EActiveInactiveStatus;
  onResolve?: ({
    result,
    closeModal,
  }: {
    result: boolean;
    closeModal: () => void;
  }) => void;
}

const ConfirmModal: FC<IConfirmModalProps> = ({
  cancelBtnProps,
  confirmBtnProps,
  title,
  onResolve,
  children,
  confirmModalType = EConfirmModalType.DELETE,
  status,
  ...props
}) => {
  const buttonTitle = () => {
    if (confirmBtnProps?.children) return confirmBtnProps.children;

    switch (confirmModalType) {
      case EConfirmModalType.DELETE:
        return buttons.delete;
      case EConfirmModalType.STATUS:
        return status === EActiveInactiveStatus.ACTIVE
          ? buttons.deactivate
          : buttons.activate;

      default:
        return buttons.confirm;
    }
  };

  const buttonColor = () => {
    switch (confirmModalType) {
      case EConfirmModalType.DELETE:
        return "error";
      case EConfirmModalType.STATUS:
        return status === EActiveInactiveStatus.ACTIVE ? "error" : "primary";
      default:
        return "warning";
    }
  };

  return (
    <ModalComponent
      small
      title={title || modals.sure}
      modalFooter={
        <>
          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: "flex-end",
              alignItems: "flex-start",
            }}
          >
            <Grid size={6}>
              <Button
                fullWidth
                variant="outlined"
                {...cancelBtnProps}
                onClick={(e) =>
                  cancelBtnProps?.onClick
                    ? cancelBtnProps?.onClick(e)
                    : props.onClose()
                }
              >
                {cancelBtnProps?.children || buttons.cancel}
              </Button>
            </Grid>
            <Grid size={6}>
              <LoadingButton
                fullWidth
                variant="contained"
                color={buttonColor()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onResolve)
                    onResolve({ result: true, closeModal: props.onClose });
                }}
                {...confirmBtnProps}
              >
                {buttonTitle()}
              </LoadingButton>
            </Grid>
          </Grid>
        </>
      }
      {...props}
    >
      {children}
    </ModalComponent>
  );
};

export default ConfirmModal;
